import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, keys, uniq } from 'lodash-es';

import Skeleton from '../../../components/Skeleton';
import { numberToPercentage } from '../../../utils/formatter';
import { aiCohortsColorsMap, aiCohortsMap } from '../configs/constants';

const formatCell = (value) => {
  if (value == null) return '\u2014';
  return numberToPercentage(value, { precision: 1 });
};

const TransitionsTable = ({ data, isPending, skeletonHeight = 210 }) => {
  if (isPending) {
    return <Skeleton height={skeletonHeight} className="d-flex" />;
  }

  if (!isPending && isEmpty(data)) {
    return <div className="transitions-table__empty">No data available</div>;
  }

  const cohortOrder = keys(aiCohortsMap);

  const allKeys = uniq(keys(data)?.reduce((acc, key) => {
    if (!data?.[key]) return acc;
    acc.push(key);
    keys(data[key]).map((nestedKey) => acc.push(nestedKey));
    return acc;
  }, []));

  const columns = cohortOrder.reduce((acc, cohort) => {
    if (!allKeys?.includes(cohort)) return acc;
    return [
      ...acc,
      {
        dataField: cohort,
        text: aiCohortsMap[cohort] || cohort,
        color: aiCohortsColorsMap[cohort] || '#000',
      },
    ];
  }, []);

  const rows = cohortOrder.reduce((acc, cohort) => {
    if (!data?.[cohort]) return acc;
    return [
      ...acc,
      {
        dataField: cohort,
        text: aiCohortsMap[cohort] || cohort,
        color: aiCohortsColorsMap[cohort] || '#000',
      },
    ];
  }, []);

  return (
    <table className="transitions-table">
      <thead>
        <tr>
          <th>
            <div className="th-cell-directions">
              <span>From</span> <span className="hicon-arrow-down" /> <span>|</span> <span>To</span>{' '}
              <span className="hicon-arrow-right" />
            </div>
          </th>
          {columns.map(({ dataField, text, color }) => (
            <th key={dataField} style={{ color }}>
              {text}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((from) => (
          <tr key={from.dataField}>
            <td style={{ color: from.color }}>{from.text}</td>
            {columns.map((to) => (
              <td
                key={to.dataField}
                className={cx({ 'text-bold': from.dataField === to.dataField })}
              >
                {formatCell(data[from.dataField]?.[to.dataField])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

TransitionsTable.propTypes = {
  data: T.object,
  skeletonHeight: T.number,
  isPending: T.bool,
};

export default TransitionsTable;
